/**
 * Rotates through `[data-rotator-item]` children on narrow screens, one visible at a time.
 * Pauses while hovered or focused, and stays on the first item with reduced motion.
 */
class EbRotator extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #index = 0;
  #mobileQuery = window.matchMedia('(max-width: 1099px)');
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.#mobileQuery.addEventListener('change', () => this.#sync(), { signal });
    this.#reducedMotion.addEventListener('change', () => this.#sync(), { signal });
    this.addEventListener('pointerenter', () => this.#stop(), { signal });
    this.addEventListener('pointerleave', () => this.#sync(), { signal });
    this.addEventListener('focusin', () => this.#stop(), { signal });
    this.addEventListener('focusout', () => this.#sync(), { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#stop();
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-rotator-item]')]);
  }

  #sync() {
    this.#stop();
    const canRotate =
      this.dataset.mode === 'rotate' &&
      this.#mobileQuery.matches &&
      !this.#reducedMotion.matches &&
      this.#items.length > 1;
    if (!canRotate) return;
    const interval = Number(this.dataset.interval) || 4000;
    this.#timer = window.setInterval(() => this.#next(), interval);
  }

  #stop() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #next() {
    const items = this.#items;
    items[this.#index]?.classList.remove('is-active');
    this.#index = (this.#index + 1) % items.length;
    items[this.#index]?.classList.add('is-active');
  }
}

if (!customElements.get('eb-rotator')) customElements.define('eb-rotator', EbRotator);
